import axios from 'axios';
import { Form, FormSubmission, OneUseLink, Knowledge, PaginatedResponse } from '../types/form';
import { API_CONFIG } from '../constants/config';

const API_BASE_URL = process.env.REACT_APP_API_URL || API_CONFIG.DEFAULT_API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

/**
 * CSRF Token Management
 * 
 * This implementation uses Django's secure pattern for SPA CSRF protection:
 * - CSRF cookies remain httpOnly (protected from XSS)
 * - Tokens are fetched via dedicated endpoint and stored in memory
 * - Tokens are automatically added to request headers
 * - Tokens are cleared on logout/login to stay synchronized with sessions
 */

// Store CSRF token in memory (not in localStorage for security)
let csrfToken: string | null = null;

// Clear stored CSRF token (called on logout/login)
function clearCsrfToken(): void {
  csrfToken = null;
}

// Get currently stored CSRF token
function getCsrfToken(): string | null {
  return csrfToken;
}

// Fetch fresh CSRF token from Django endpoint
async function fetchCsrfToken(): Promise<string | null> {
  try {
    const response = await axios.get('/auth/csrf/', {
      baseURL: API_BASE_URL,
      withCredentials: true,
    });
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
}

// Add CSRF token to requests automatically
api.interceptors.request.use(
  async (config) => {
    // Skip CSRF token for the CSRF endpoint itself to avoid circular dependency
    if (config.url?.includes('/auth/csrf/')) {
      return config;
    }
    
    let token = getCsrfToken();
    
    // If no token stored, fetch a fresh one from the server
    if (!token) {
      token = await fetchCsrfToken();
    }
    
    // Add CSRF token to request headers if available
    if (token) {
      config.headers[API_CONFIG.CSRF_HEADER_NAME] = token;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

export const formsApi = {
  // Authentication
  getCsrfToken: fetchCsrfToken,
  clearCsrfToken: clearCsrfToken,
  login: (email: string, password: string) => 
    api.post('/auth/login/', { email, password }),
  logout: () => api.post('/auth/logout/'),
  getCurrentUser: () => api.get('/auth/user/'),
  changePassword: (current_password: string, new_password: string) =>
    api.post('/auth/change-password/', { current_password, new_password }),
  
  // Forms
  getForms: () => api.get<Form[]>('/forms/'),
  getSharedForms: () => api.get<Form[]>('/forms/shared/'),
  getForm: (id: string) => api.get<Form>(`/forms/${id}/`),
  createForm: (data: Partial<Form>) => api.post<Form>('/forms/', data),
  duplicateForm: (id: string) => api.post<Form>(`/forms/${id}/duplicate/`),
  updateForm: (id: string, data: Partial<Form>) => api.put<Form>(`/forms/${id}/`, data),
  patchForm: (id: string, data: Partial<Form>) => api.patch<Form>(`/forms/${id}/`, data),
  deleteForm: (id: string) => api.delete(`/forms/${id}/`),
  
  // Form Fields
  getFormFields: (formId: string) => api.get(`/forms/${formId}/fields/`),
  createFormField: (formId: string, data: any) => api.post(`/forms/${formId}/fields/`, data),
  updateFormField: (formId: string, fieldId: string, data: any) => 
    api.put(`/forms/${formId}/fields/${fieldId}/`, data),
  deleteFormField: (formId: string, fieldId: string) => 
    api.delete(`/forms/${formId}/fields/${fieldId}/`),
  
  // Form Submissions
  getFormSubmissions: (formId: string) => api.get<FormSubmission[]>(`/forms/${formId}/submissions/`),
  exportSubmissionsPdf: (formId: string, submissionId?: string) => {
    const url = submissionId 
      ? `/forms/${formId}/submissions/pdf/?submission_id=${submissionId}`
      : `/forms/${formId}/submissions/pdf/`;
    return api.get(url, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf',
      },
    });
  },
  
  // Custom Links
  getOneUseLinks: (formId: string, page: number = 1) =>
    api.get<PaginatedResponse<OneUseLink>>(`/forms/${formId}/one-use-links/?page=${page}`),
  createOneUseLink: (formId: string, name: string, maxUses: number = 1, preSelectedFields: Record<string, any> = {}) => 
    api.post<OneUseLink>(`/forms/${formId}/one-use-links/`, { name, max_uses: maxUses, pre_selected_fields: preSelectedFields }),
  deleteOneUseLink: (linkId: string) => api.delete(`/one-use-links/${linkId}/`),
  
  // Knowledge
  getKnowledge: () => api.get<Knowledge[]>('/knowledge/'),
  createKnowledge: (data: Partial<Knowledge>) => api.post<Knowledge>('/knowledge/', data),
  updateKnowledge: (id: string, data: Partial<Knowledge>) => api.put<Knowledge>(`/knowledge/${id}/`, data),
  deleteKnowledge: (id: string) => api.delete(`/knowledge/${id}/`),
  
  // Public API - Unified Clean URLs
  getPublicForm: (identifier: string) => {
    return api.get<Form>(`/public/${identifier}/`);
  },
  submitPublicForm: (identifier: string, data: any) => {
    return api.post(`/public/${identifier}/submit/`, data);
  },
  
  // Generic HTTP passthroughs (used by webhook/integration settings)
  get: (url: string) => api.get(url),
  post: (url: string, data?: any) => api.post(url, data),
  put: (url: string, data?: any) => api.put(url, data),
  patch: (url: string, data?: any) => api.patch(url, data),
  delete: (url: string) => api.delete(url),
};

export default api;